import React from 'react';
import type { Control } from 'react-hook-form';

import Checkbox from '@components/uikit/Checkbox/Checkbox';

import useTranslation from '@hooks/useTranslation';

import type { IEmailEventDictionary } from '@model/eventsDictionary';

import { useActionItemMetaInfoQuery } from '@services/useActionItemMetaInfoQuery';
import { useUserEmailEventDictionaryQuery } from '@services/user/useUserEmailEventDictionaryQuery';

import { USER_FIELDS } from '../../constants/schemaUser';
import AdditionalInfoSkeleton from './AdditionalInfoSkeleton';
import style from './style.module.scss';

interface IProps {
  user: { id: number; simpleEvents: IEmailEventDictionary[] };
  control: Control<any>;
  isLoading: boolean;
  allFacilities?: number;
}

const AdditionalInfo: React.FC<IProps> = ({ user, control, isLoading, allFacilities }) => {
  const { t } = useTranslation();

  const { data: aiMetaInfoData } = useActionItemMetaInfoQuery();

  const urgenciesList = aiMetaInfoData?.actionListUrgencies.list || [];

  const { data: emailEventsData } = useUserEmailEventDictionaryQuery();

  const isUser = !!user?.id;
  const simpleEventsList = isUser ? user?.simpleEvents : emailEventsData?.simpleEventsDictionary;

  const filteredSimpleEvents = () => {
    const simpleEventsArr: { id: number; name: string }[] = [];

    simpleEventsList?.forEach((event) => {
      const name = event.name.toLowerCase();

      if (name.includes('reminder')) {
        simpleEventsArr.push({ id: event.id, name: event.name });
      }
      if (name.includes('deactivate')) {
        simpleEventsArr.push({ id: event.id, name: 'Send Emails About Deactivated Equipment' });
      }
    });

    return simpleEventsArr;
  };

  const simpleEvents = filteredSimpleEvents();

  return (
    <div className={style.additionalInfo}>
      <div>
        <div className={style.blockTitle}>{t('Additional email notification')}</div>
        {isLoading ? (
          <AdditionalInfoSkeleton />
        ) : (
          <div className={style.blockContainer}>
            {simpleEvents.map((event) => (
              <Checkbox
                key={event.id}
                name={`${USER_FIELDS.SIMPLE_EMAIL_SUBSCRIPTIONS}[${event.id}]`}
                control={control}
                label={t(event.name)}
                withHelperText={false}
              />
            ))}
            {!!allFacilities && (
              <Checkbox
                name={USER_FIELDS.DEFAULT_SUBSCRIBED}
                control={control}
                label={t('Automatically subscribe to email alerts for new facilities')}
                withHelperText={false}
              />
            )}
            <Checkbox
              name={USER_FIELDS.RECEIVE_OWN_EMAILS}
              control={control}
              label={t('Send emails for comments and reports submitted by this user')}
              withHelperText={false}
            />
          </div>
        )}
      </div>
      <div>
        <div className={style.blockTitle}>{t('Mobile App Notifications')}</div>
        {isLoading ? (
          <AdditionalInfoSkeleton />
        ) : (
          <div className={style.blockContainer}>
            {!!urgenciesList.length &&
              urgenciesList.map((urgency) => (
                <Checkbox
                  key={urgency.name}
                  className={style[`checkbox${urgency.name}`]}
                  name={`${USER_FIELDS.ACTION_LIST_URGENCIES}[${urgency.id}]`}
                  control={control}
                  label={urgency.name || ''}
                  withHelperText={false}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdditionalInfo;
